import React, { JSX } from 'react'
import { AnalyticsMetafield, CountryTotals } from 'types/api'
import { EmptyDataCard } from 'components/EmptyDataCard'
import {
  ComposableMap,
  Geographies,
  Geography
} from 'react-simple-maps'
import { Tooltip } from 'react-tooltip'
import { cleanVariables, getCountryNameFromCode, graphqlRequest, moneyFormatter, numberFormatter } from 'lib/helpers'
import { useMediaQuery } from 'react-responsive'
import { useQuery } from '@tanstack/react-query'
import getCountryTotals from '../graphql/getCountryTotals.graphql'
import { RefreshingBlock } from './Loading'

const geoUrl = '/110m-world-topo.json'

export interface WorldMapProps {
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  client: AnalyticsMetafield | null
}

export const WorldMap = ({ filters, token, client }: WorldMapProps): JSX.Element => {
  const isMobile = useMediaQuery({ maxWidth: 639 })

  const {
    data,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['dashboard', 'countryTotals', filters, client?.id],

    queryFn: async () => {
      if (token == null) return

      const cleanVar = cleanVariables({
        clientId: client?.id,
        projectId: filters.project?.id,
        productIds: filters?.productOption?.map(p => p.id),
        startDate: filters.range.startDate,
        endDate: filters.range.endDate
      })
      const { countryTotals } = await graphqlRequest<{ countryTotals: CountryTotals[] }>(
        getCountryTotals, cleanVar, token
      )

      return countryTotals
    },

    enabled: token != null && client != null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const mappedCountryData = new Map(
    data?.map(item => [item.country, item])
  )

  const desktopMap = (
    <ComposableMap
      projection='geoEquirectangular'
      width={900}
      height={300}
      projectionConfig={{ rotate: [-10, 0, 0], scale: 120, center: [0, 15] }}
    >
      <Geographies geography={geoUrl}>
        {({ geographies }) =>
          geographies.map(geo => {
            const countryData = mappedCountryData?.get(geo.properties.iso_a2)
            const name = countryData?.countryName ?? getCountryNameFromCode(geo.properties.iso_a2)
            const hasSales = (countryData != null && name != null)
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                stroke='var(--stroke)'
                strokeWidth={1}
                data-tooltip-id='map-tooltip'
                data-tooltip-float='true'
                data-tooltip-html={hasSales ? `<p>${name}</p><p>${moneyFormatter(countryData.sales)}</p><p>${numberFormatter(countryData.quantity)} sales</p>` : ''}
                data-tooltip-content={hasSales ? `${name}: $${countryData.sales.toFixed(2)}, ${countryData.quantity} sales` : ''}
                style={{
                  default: {
                    fill: hasSales ? 'var(--fillSales)' : 'var(--fillNoSales)',
                    outline: 'none'
                  },
                  hover: {
                    fill: hasSales ? 'var(--hoverSales)' : 'var(--hoverNoSales)',
                    outline: 'none'
                  },
                  pressed: { fill: hasSales ? 'var(--pressedSales)' : 'var(--pressedNoSales)', outline: 'none' }

                }}
              />
            )
          })}
      </Geographies>
    </ComposableMap>
  )

  const mobileMap = (
    <ComposableMap
      height={490}
      projection='geoNaturalEarth1'
    >
      <Geographies geography={geoUrl}>
        {({ geographies }) =>
          geographies.map(geo => {
            const countryData = mappedCountryData?.get(geo.properties.iso_a2)
            const name = countryData?.countryName ?? getCountryNameFromCode(geo.properties.iso_a2)
            const hasSales = (countryData != null && name != null)
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                stroke='var(--stroke)'
                strokeWidth={1}
                data-tooltip-id='map-tooltip'
                data-tooltip-float='true'
                data-tooltip-html={hasSales ? `<p>${name}</p><p>${moneyFormatter(countryData.sales)}</p><p>${numberFormatter(countryData.quantity)} sales</p>` : ''}
                data-tooltip-content={hasSales ? `${name}: $${countryData.sales.toFixed(2)}, ${countryData.quantity} sales` : ''}
                style={{
                  default: {
                    fill: hasSales ? 'rgba(252, 180, 21, .5)' : 'rgba(4, 4, 5, 0.05)',
                    outline: 'none'
                  },
                  hover: {
                    fill: hasSales ? 'rgba(252, 180, 21, 1)' : 'rgba(4, 4, 5, 0.05)',
                    outline: 'none'
                  },
                  pressed: { fill: hasSales ? 'rgba(252, 180, 21, 1)' : 'rgba(4, 4, 5, 0.05)', outline: 'none' }
                }}
              />
            )
          })}
      </Geographies>
    </ComposableMap>
  )

  const noData = <EmptyDataCard />

  const withData = (
    <>
      <div className='flex items-center justify-between px-4 pt-2 pb-3'>
        <h2 className='text-sm font-medium'>Customers around the world</h2>
      </div>
      {isMobile ? mobileMap : desktopMap}
      <Tooltip id='map-tooltip' />
    </>
  )

  return (
    <div className='w-full md:aspect-[9/3] relative
  [--stroke:var(--color-lm-bg-main)]
  [--fillNoSales:var(--color-lm-bg-accent)]
  [--fillSales:#fcb41580]
  [--hoverNoSales:var(--color-lm-bg-accent)]
  [--hoverSales:var(--color-brand)]
  [--pressedNoSales:var(--color-lm-bg-accent)]
  [--pressedSales:var(--color-brand)]

  dark:[--stroke:var(--color-dm-bg-main)]
  dark:[--fillNoSales:var(--color-dm-bg-accent)]    /* 0.04 */
  dark:[--fillSales:#fcb41559]      /* 0.35 */
  dark:[--hoverNoSales:var(--color-dm-bg-accent)]   /* 0.06 */
  dark:[--hoverSales:var(--color-brand)]     /* 0.90 */
  dark:[--pressedNoSales:var(--color-dm-bg-accent)] /* 0.08 */
  dark:[--pressedSales:var(--color-brand)]
  '
    >
      {isFetching && RefreshingBlock}
      {(mappedCountryData !== null && mappedCountryData.size > 0 && !isError) ? withData : noData}
    </div>
  )
}
